import Card from "@material-ui/core/Card"
import CardContent from "@material-ui/core/CardContent"
import CardActions from "@material-ui/core/CardActions"
import Button from "@material-ui/core/Button"
import TextField from "@material-ui/core/TextField"
import Typography from "@material-ui/core/Typography"
import { useTheme } from "@material-ui/core"
import { useIsSmallScreen } from "../../../hooks/use-is-small-screen"
import { useUserMutations } from "../../hooks/use-user-mutations"
import React from "react"
import { z } from "zod"
import { UserViewModel } from "../../api/user"
import { EmailChangeRequestViewModel } from "../../api/email"
import { useGetEmailChangeRequestQuery } from "../../api"
import { useCountdown } from "../../../hooks/use-countdown"
import { Alert, AlertTitle } from "@material-ui/lab"
import { Clock } from "react-feather"
import { useTranslations } from "./use-translations"

const otpRegex = /^(\d\d\d)(\d\d\d)$/
const valueToOtp = (value: string) => value.trim().replace(/\s+/g, "")

const OtpSchema = z
  .string()
  .refine((value) => {
    const otp = valueToOtp(value)
    return otp.length === 6 && otpRegex.test(otp)
  }, "Invalid OTP.")
  .transform((value) => {
    const otp = valueToOtp(value)
    return otp.replace(otpRegex, "$1 $2")
  })

const useVefificationCodeExpiryCountdown = (expiresAt: Date) => {
  const countdown = useCountdown(expiresAt)
  const { refetch } = useGetEmailChangeRequestQuery()

  React.useEffect(() => {
    if (countdown.distance <= 0) {
      refetch()
    }
  }, [countdown.distance, refetch])

  return countdown
}

const EmailChangeRequest = ({ user, emailChangeRequest }: EmailChangeRequestProps) => {
  const isSmallScreen = useIsSmallScreen("xs")
  const theme = useTheme()
  const { verifyEmail, resendOtp, cancelEmailChangeRequest } = useUserMutations()
  const [otp, setOtp] = React.useState("")
  const [error, setError] = React.useState<string | null>(null)
  const translations = useTranslations({ emailChangeRequestNewEmail: emailChangeRequest.newEmail })

  const handleResendOtp = () => resendOtp(user.id)
  const handleCancelEmailChangeRequest = () => cancelEmailChangeRequest(user.id)

  const handleVerifyEmail = async (otp: string) => {
    await verifyEmail(user.id, { newEmail: emailChangeRequest.newEmail, otp })
  }

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    const parsedOtp = OtpSchema.safeParse(otp)
    if (!parsedOtp.success) {
      setError(parsedOtp.error.errors[0].message)
      return
    }

    setError(null)
    setOtp("")
    return handleVerifyEmail(parsedOtp.data)
  }

  return (
    <Card variant="outlined">
      <form onSubmit={handleSubmit}>
        <CardContent>
          <Typography variant="subtitle1" style={{ marginBottom: 12, fontWeight: 500 }}>
            {translations.emailChangeRequestHeader}
          </Typography>
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
            {translations.emailChangeRequestText}
          </Typography>
          <VerificationCodeExpiryCountDown emailChangeRequest={emailChangeRequest} />
          <TextField
            label={translations.enterVerificationCode}
            value={otp}
            variant="outlined"
            error={Boolean(error)}
            helperText={error}
            onChange={(event) => {
              setOtp(event.target.value)
              setError(null)
            }}
            style={{ display: emailChangeRequest.isExpired ? "none" : "block" }}
            fullWidth
          />
        </CardContent>
        <CardActions style={{ padding: "0 16px 16px 16px", flexWrap: "wrap" }}>
          <Button
            type="submit"
            size="small"
            variant="contained"
            color="primary"
            style={{ display: emailChangeRequest.isExpired ? "none" : "block" }}
            disabled={emailChangeRequest.isExpired}
          >
            {translations.verify}
          </Button>
          <div
            style={{
              width: isSmallScreen ? "100%" : "auto",
              marginLeft: isSmallScreen || emailChangeRequest.isExpired ? 0 : "auto",
              display: "flex",
              gap: 8,
              marginTop: isSmallScreen ? 8 : 0,
            }}
          >
            <Button
              size="small"
              onClick={handleResendOtp}
              variant={emailChangeRequest.isExpired ? "contained" : "outlined"}
              color={emailChangeRequest.isExpired ? "primary" : "default"}
              style={{ borderColor: theme.palette.divider }}
            >
              {translations.sendCode}
            </Button>
            <Button
              size="small"
              onClick={handleCancelEmailChangeRequest}
              variant="outlined"
              style={{ borderColor: theme.palette.divider }}
            >
              {translations.cancel}
            </Button>
          </div>
        </CardActions>
      </form>
    </Card>
  )
}

const VerificationCodeExpiryCountDown = ({
  emailChangeRequest,
}: {
  emailChangeRequest: EmailChangeRequestViewModel
}) => {
  const theme = useTheme()
  const countdown = useVefificationCodeExpiryCountdown(emailChangeRequest.expiresAt)
  const translations = useTranslations({ countdown: countdown.formatted })
  const isExpired = countdown.distance <= 0
  const severity = isExpired ? "error" : "info"
  const alertMessage = isExpired ? translations.codeExpiredText : translations.codeCountdownText

  return (
    <Alert
      icon={<Clock size={20} color={theme.palette.common.black} style={{ marginTop: 2 }} />}
      severity={severity}
      style={{ marginBottom: 16 }}
    >
      <AlertTitle>{alertMessage}</AlertTitle>
    </Alert>
  )
}

export default EmailChangeRequest

type EmailChangeRequestProps = {
  user: UserViewModel
  emailChangeRequest: EmailChangeRequestViewModel
}
